/**
 * "Native back is safe" certification for the all-saves board (/liked).
 *
 * The desktop account dropdown's "all saves" row is a plain <a href="/liked">
 * that FULL-page-loads (V4AccountDropdown is deliberately react-router-
 * independent — it renders without a <Router> in isolated tests/stories). On
 * that load react-router's history idx is 0, so AllSavedPage can't tell a
 * dropdown click — where the BROWSER stack still holds the source page behind
 * /liked — from a deep link / refresh, where nothing is behind.
 *
 * Certification travels in TWO stages so it is both (a) carried across the
 * full-page load and (b) durable across a later refresh/remount of /liked:
 *
 *  1. TRANSIENT hand-off, BOUND to the navigation — the dropdown arms a
 *     sessionStorage marker holding {t: Date.now(), src: <the source page URL>}
 *     right before the load. sessionStorage is the only channel that survives a
 *     same-tab full-page navigation. It is armed ONLY for a plain, primary-button,
 *     same-tab click to the same-origin /liked route. On arrival the marker
 *     certifies ONLY when `document.referrer` (the page the /liked load actually
 *     came FROM — the full same-origin URL under the app's
 *     `strict-origin-when-cross-origin` policy) matches the stored `src`. So an
 *     orphaned marker (a click whose navigation was cancelled/failed) can never
 *     certify a LATER, unrelated /liked visit: that visit's referrer won't match
 *     the source that armed the marker (a typed URL / deep link / cross-origin
 *     entry has no matching same-origin referrer → fail closed → fallback). The
 *     referrer/source match is the SOLE certification gate — there is no elapsed-
 *     time cutoff, so a valid marker survives a slow/stalled load or a suspended
 *     tab; the stored timestamp is validated only for sanity (finite, not in the
 *     future), never as an arrival deadline.
 *  2. DURABLE cert — on a certified arrival AllSavedPage stamps a namespaced flag
 *     onto the /liked history ENTRY (`window.history.state`) and clears the
 *     transient marker. history.state is restored on reload, so a refresh (or any
 *     genuine remount) of that entry still reads the cert — where the transient
 *     marker, consumed once, is already gone. The cert is scoped to THAT entry, so
 *     it can never certify a different /liked entry, and it needs no referrer
 *     re-check (its presence IS the proof of an earlier certified arrival).
 *
 * AllSavedPage treats EITHER signal as "arrived via the instrumented load" and
 * uses native back (navigate(-1)) — restoring the source page WITH its scroll —
 * instead of a fallback landing.
 */
const KEY = 'thea:savedNativeBackSafe';
// Namespaced field on the /liked history entry's state (survives reload).
const HISTORY_CERT_FIELD = 'theaLikedNativeBackSafe';

interface StoredMarker {
  t: number;
  src: string;
}

interface ClickLike {
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  button?: number;
  defaultPrevented?: boolean;
  currentTarget?: { target?: string; href?: string } | null;
}

// Canonical form for comparing a stored source URL against document.referrer:
// origin + pathname + search, FRAGMENT STRIPPED (document.referrer never carries a
// fragment, so the stored src must drop it too for an apples-to-apples match). An
// unresolvable input normalizes to '' (which never matches a real referrer).
function normalizeUrl(u: string): string {
  try {
    const url = new URL(u, window.location.origin);
    return url.origin + url.pathname + url.search;
  } catch {
    return '';
  }
}

// True only if `href` resolves to the same-origin /liked route; an unresolvable
// href fails closed. When href is ABSENT (a synthetic/keyboard activation with no
// element) we cannot check the destination, so we do not reject on that alone — a
// real anchor click always carries a resolved href, which is the case that matters
// for the wrong-destination guard.
function resolvesToLiked(href: string): boolean {
  try {
    const u = new URL(href, window.location.origin);
    return u.origin === window.location.origin && u.pathname === '/liked';
  } catch {
    return false;
  }
}

/**
 * Arm the TRANSIENT marker — ONLY for a plain, primary-button, same-tab click to
 * the /liked route:
 *  - `defaultPrevented` → an ancestor already cancelled the navigation, so the
 *    full-page load won't happen; don't arm.
 *  - a modified click (cmd/ctrl/shift/alt), a middle-click, or target=_blank
 *    opens a NEW tab and does NOT navigate this document → arming would leave a
 *    stale flag here.
 *  - a resolved href that is NOT same-origin /liked → a marker must never certify
 *    a /liked entry it was not created for.
 * Stores the SOURCE page URL alongside the timestamp so arrival can bind the
 * certification to the navigation that actually reached /liked (see peek).
 * Keyboard activation (Enter on the anchor) fires a plain click and is allowed.
 */
export function markSavedNativeBackSafe(e: ClickLike): void {
  if (e.defaultPrevented) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  if (e.button != null && e.button !== 0) return;
  const target = e.currentTarget?.target;
  if (target && target !== '_self') return;
  const href = e.currentTarget?.href;
  if (href != null && !resolvesToLiked(href)) return;
  try {
    const marker: StoredMarker = {
      t: Date.now(),
      src: normalizeUrl(window.location.href),
    };
    window.sessionStorage.setItem(KEY, JSON.stringify(marker));
  } catch {
    // sessionStorage unavailable (private mode / disabled) → AllSavedPage falls
    // back to its deterministic landing. Never throw from a click handler.
  }
}

/**
 * Read the TRANSIENT marker WITHOUT removing it, and decide whether it certifies
 * THIS /liked arrival. Certifies only when the marker is present, well-formed,
 * carries a sane timestamp (finite, not in the future — a clock rollback reads
 * false), AND is bound to the navigation: `document.referrer` (same-origin, full
 * path under the app's `strict-origin-when-cross-origin` policy) matches the
 * stored source. There is NO elapsed-time cutoff — the referrer/source match is
 * the gate, so a valid marker survives a slow load or a suspended tab (an old
 * marker whose referrer still matches its source is, by construction, a real
 * navigation FROM that source, so native-back is correct).
 * A missing / cross-origin / mismatched referrer fails CLOSED. Pure (no mutation),
 * so it is safe from render / a useState initializer — Strict Mode's double render
 * (and any discarded concurrent render) read the same value both times and can't
 * "consume" it early.
 *
 * (This peek/consume split replaced a single read+remove used in the render body,
 * which Strict Mode's double render defeated: the second pass found the flag
 * already removed and committed `false`.)
 */
export function peekSavedNativeBackSafe(): boolean {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (raw == null) return false;
    let marker: StoredMarker | null = null;
    try {
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed.t === 'number' &&
        typeof parsed.src === 'string'
      ) {
        marker = parsed as StoredMarker;
      }
    } catch {
      return false; // legacy / garbage value → fail safe
    }
    if (!marker) return false;
    // Sanity only — NOT an arrival deadline: a finite, non-future timestamp. The
    // referrer/source match below is the certification gate, so a valid marker is
    // never rejected merely for being old (a slow load, a suspended/slept tab).
    if (!Number.isFinite(marker.t) || marker.t > Date.now()) return false;
    const referrer = document.referrer;
    if (!referrer) return false; // no referrer → not bound → fail closed
    return normalizeUrl(referrer) === marker.src;
  } catch {
    return false;
  }
}

/**
 * Read the DURABLE cert off the current history entry (`window.history.state`).
 * Pure. This is what survives a refresh/remount of /liked after the transient
 * marker has been consumed; its presence is proof of an earlier certified arrival,
 * so it needs no referrer re-check.
 */
export function peekLikedHistoryCert(): boolean {
  try {
    const state = window.history.state as Record<string, unknown> | null;
    return !!state && state[HISTORY_CERT_FIELD] === true;
  } catch {
    return false;
  }
}

/**
 * Consume the arrival on /liked, exactly once:
 *  - if the transient marker CERTIFIES this arrival (present, unexpired, and its
 *    stored source matches document.referrer), PERSIST the durable cert onto the
 *    current history entry (so a later refresh/remount still restores the source),
 *    preserving react-router's existing state fields;
 *  - ALWAYS clear the transient marker so leaving /liked by any route can't leave
 *    it armed for a later deep link.
 * Idempotent and side-effect-safe under Strict Mode's setup→cleanup→setup
 * double-invoke: a second call sees the marker already gone and the cert already
 * present, so it neither re-stamps nor throws.
 *
 * CAVEAT (react-router v6): react-router serializes only {usr,key,idx} onto
 * history.state, so ANY react-router push/replace that re-writes the bare /liked
 * entry (e.g. a future `setSearchParams(..., {replace:true})` for a filter/tab on
 * /liked) would DROP this namespaced cert. Today nothing replaces the base /liked
 * entry (the PDP stack only touches `?pdp=N` entries), so the cert is safe; if
 * that changes, re-stamp by calling this again after the write. handleBack also
 * reads the cert LIVE at click time, so it reflects whatever entry is standing.
 */
export function consumeSavedNativeBackSafe(): void {
  try {
    const certified = peekSavedNativeBackSafe();
    window.sessionStorage.removeItem(KEY);
    if (certified && !peekLikedHistoryCert()) {
      const state =
        (window.history.state as Record<string, unknown> | null) ?? {};
      window.history.replaceState(
        { ...state, [HISTORY_CERT_FIELD]: true },
        '',
      );
    }
  } catch {
    // storage / history unavailable — the page falls back to its deterministic
    // landing. Never throw from a mount effect.
  }
}
